import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { auth, firestore } from '../services/firebase';
import { Routine } from '../types';
import { useNotification } from '../context/NotificationContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import React from 'react';

export const useSharedRoutines = (inRoutinesScreen = false) => {
  const [sharedRoutines, setSharedRoutines] = useState<Routine[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const previousRoutineIds = useRef<string[]>([]);
  const { showToast } = useNotification();

  // Si estamos en la pantalla de rutinas, resetear el contador
  useFocusEffect(
    React.useCallback(() => {
      if (inRoutinesScreen) {
        resetNotificationCount();
      }
    }, [inRoutinesScreen])
  );

  // Función para guardar las rutinas ya notificadas
  const saveNotifiedRoutines = async (routineIds: string[]) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;
      
      await AsyncStorage.setItem(`@sharedRoutines_${userId}`, JSON.stringify(routineIds));
    } catch (error) {
      console.error("Error al guardar estado de rutinas compartidas:", error);
    }
  };

  // Función para obtener las rutinas ya notificadas
  const getNotifiedRoutines = async (): Promise<string[]> => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return [];
      
      const viewed = await AsyncStorage.getItem(`@sharedRoutines_${userId}`);
      return viewed ? JSON.parse(viewed) : [];
    } catch (error) {
      console.error("Error al obtener estado de rutinas compartidas:", error);
      return [];
    }
  };

  // Función para resetear el contador de notificaciones
  const resetNotificationCount = async () => {
    try {
      // Guardar todas las rutinas actuales como ya vistas
      await saveNotifiedRoutines(sharedRoutines.map(routine => routine.id));
      setNotificationCount(0);
    } catch (error) {
      console.error("Error al resetear contador:", error);
    }
  };

  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setLoading(false);
      return;
    }

    // Cargar rutinas ya notificadas al inicio
    getNotifiedRoutines().then(notifiedIds => {
      previousRoutineIds.current = notifiedIds;
    });

    // Crear una consulta para rutinas donde authorizedUsers contiene el ID del usuario
    const sharedRoutinesQuery = query(
      collection(firestore, 'routines'),
      where('authorizedUsers', 'array-contains', userId)
    );
    
    // Configurar un listener en tiempo real para cambios en las rutinas compartidas
    const unsubscribe = onSnapshot(sharedRoutinesQuery, async (querySnapshot) => {
      const routinesData: Routine[] = [];
      
      querySnapshot.forEach((doc) => {
        routinesData.push({ id: doc.id, ...doc.data() } as Routine);
      });
      
      setSharedRoutines(routinesData);
      
      // Obtenemos las rutinas ya vistas por el usuario
      const notifiedIds = await getNotifiedRoutines();
      
      // Encontrar las nuevas rutinas (las que están en routinesData pero no en notifiedIds)
      const currentIds = routinesData.map(routine => routine.id);
      const newRoutines = routinesData.filter(routine => !notifiedIds.includes(routine.id));
      
      // Actualizar el contador de notificaciones
      setNotificationCount(newRoutines.length);
      
      // Verificar si se eliminó alguna rutina y actualizar la lista guardada
      if (notifiedIds.some(id => !currentIds.includes(id))) {
        const updatedNotifiedIds = notifiedIds.filter(id => currentIds.includes(id));
        previousRoutineIds.current = updatedNotifiedIds;
        await saveNotifiedRoutines(updatedNotifiedIds);
      }
      
      setLoading(false);
    }, (error) => {
      console.error("Error al obtener rutinas compartidas:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return {
    sharedRoutines,
    sharedRoutinesCount: notificationCount,
    loading,
    resetNotificationCount
  };
}; 